"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";

type LiveIndex = {
  index: string;
  code: string;
  value: number | null;
  change: number | null;
  pct_change: number | null;
};

type LivePayload = {
  source_url: string;
  as_of: string | null;
  indices: LiveIndex[];
};

type WindowSupport = {
  has20D?: boolean;
  has60D?: boolean;
  has90D?: boolean;
  primaryRank?: string;
};

type RSMetricKey = "RS20D" | "RS60D" | "RS90D";
type StockTab = "leaders" | "laggards";

type RelativeStrengthSummaryRow = {
  ticker: string;
  RS20D: number | null;
  RS60D: number | null;
  RS90D: number | null;
  sector: string;
};

type RelativeStrengthSummaryPayload = {
  indexName: string;
  period: {
    from: string;
    to: string;
  };
  windowSupport?: WindowSupport;
  rsBreadth: {
    positiveRS20D_pct: number | null;
    positiveRS60D_pct: number | null;
    positiveRS90D_pct: number | null;
  };
  rsAverages: {
    avgRS20D: number | null;
    avgRS60D: number | null;
    avgRS90D: number | null;
  };
  leaders: RelativeStrengthSummaryRow[];
  laggards: RelativeStrengthSummaryRow[];
  sectorLeaders: {
    bestSector: { name: string; avgRS60D: number | null };
    worstSector: { name: string; avgRS60D: number | null };
  };
};

type SectorRelativeStrengthRow = {
  sector: string;
  RS20D: number | null;
  RS60D: number | null;
  RS90D: number | null;
  weightPct?: number | null;
};

type SectorRelativeStrengthPayload = {
  indexName: string;
  period: { from: string; to: string };
  windowSupport?: WindowSupport;
  sectorRS: SectorRelativeStrengthRow[];
  summary: {
    positiveRS60D_pct: number | null;
    bestSector: { name: string; RS60D: number | null };
    worstSector: { name: string; RS60D: number | null };
    avgRS60D: number | null;
    dispersionRS60D: number | null;
  };
};

const fmt = (value: number, digits = 2) =>
  value.toLocaleString(undefined, { maximumFractionDigits: digits });

const fmtMaybe = (value: number | null | undefined, digits = 2) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "—";
  return fmt(value, digits);
};

const fmtPct = (value: number | null | undefined, digits = 2) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "—";
  return `${(value * 100).toFixed(digits)}%`;
};

const fmtPctValue = (value: number | null | undefined, digits = 1) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "—";
  return `${fmt(value, digits)}%`;
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && !Number.isNaN(value);

const valueColor = (value: number | null | undefined, neutralThreshold = 0) => {
  if (typeof value !== "number" || Number.isNaN(value) || value === 0) return "var(--color-text-muted)";
  if (Math.abs(value) < neutralThreshold) return "var(--color-text-muted)";
  return value > 0 ? "var(--color-success)" : "var(--color-danger)";
};
const rsColor = (value: number | null | undefined) => valueColor(value, 0.01);

const RS_LABELS: Record<RSMetricKey, string> = {
  RS20D: "15 sessions",
  RS60D: "30 sessions",
  RS90D: "45 sessions",
};

const RS_WINDOW_OPTIONS: Array<{ key: RSMetricKey; label: string; shortLabel: string }> = [
  { key: "RS20D", label: "15 sessions", shortLabel: "15S" },
  { key: "RS60D", label: "30 sessions", shortLabel: "30S" },
  { key: "RS90D", label: "45 sessions", shortLabel: "45S" },
];

const INDEX_OPTIONS = [
  { value: "KSE-100", label: "KSE-100", description: "Benchmark" },
  { value: "KSE-ALL-SHARES", label: "KSE All Shares", description: "Full market" },
  { value: "KMI-30", label: "KMI-30", description: "Islamic 30" },
  { value: "MII30", label: "MII30", description: "Investable Islamic" },
  { value: "PSXDIV20", label: "PSXDIV20", description: "Dividend 20" },
] as const;

const getPrimaryRsKey = (support?: WindowSupport | null): RSMetricKey => {
  if (support?.primaryRank === "RS60D" || support?.primaryRank === "RS90D") {
    return support.primaryRank;
  }
  return "RS20D";
};

const getRsValue = (
  row: { RS20D?: number | null; RS60D?: number | null; RS90D?: number | null } | null | undefined,
  key: RSMetricKey,
) => {
  if (!row) return null;
  const value = row[key];
  return isFiniteNumber(value) ? value : null;
};

const isWindowSupported = (key: RSMetricKey, support?: WindowSupport | null) => {
  if (!support) return true;
  if (key === "RS20D") return support.has20D !== false;
  if (key === "RS60D") return support.has60D !== false;
  return support.has90D !== false;
};

const sortByRs = <T extends { RS20D?: number | null; RS60D?: number | null; RS90D?: number | null }>(
  rows: T[],
  key: RSMetricKey,
  direction: "asc" | "desc" = "desc",
) => {
  const factor = direction === "desc" ? -1 : 1;
  return [...rows]
    .filter((row) => getRsValue(row, key) !== null)
    .sort((a, b) => ((getRsValue(a, key) ?? 0) - (getRsValue(b, key) ?? 0)) * factor);
};

const normalizeCode = (value: string) => value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
const parseMaybeNumber = (value: unknown): number | null => {
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const normalizeLivePayload = (raw: unknown): LivePayload | null => {
  if (!raw || typeof raw !== "object") return null;
  const payload = raw as Record<string, unknown>;
  const rawIndices = Array.isArray(payload.indices) ? payload.indices : [];
  const indices: LiveIndex[] = rawIndices
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const row = entry as Record<string, unknown>;
      const index = String(row.index ?? row.name ?? row.code ?? "").trim();
      const code = String(row.code ?? row.index ?? row.name ?? "").trim();
      if (!index && !code) return null;
      return {
        index,
        code,
        value: parseMaybeNumber(row.value ?? row.index_value ?? row.current_value ?? row.level),
        change: parseMaybeNumber(row.change ?? row.point_change ?? row.net_change),
        pct_change: parseMaybeNumber(row.pct_change ?? row.change_pct ?? row.percent_change ?? row.percent),
      };
    })
    .filter((row): row is LiveIndex => row !== null);

  return {
    source_url: typeof payload.source_url === "string" ? payload.source_url : "",
    as_of:
      typeof payload.as_of === "string"
        ? payload.as_of
        : typeof payload.snapshot_at === "string"
          ? payload.snapshot_at
          : typeof payload.snapshot_date === "string"
            ? payload.snapshot_date
            : null,
    indices,
  };
};

const INDEX_CODE_MAP: Record<string, string> = {
  "KSE-100": "KSE100",
  "KSE-ALL-SHARES": "ALLSHR",
  "KMI-30": "KMI30",
  "MII30": "MII30",
  "PSXDIV20": "PSXDIV20",
};

const monthCoverageSummary = (from: string, to: string) => {
  const start = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return null;

  const oneDay = 24 * 60 * 60 * 1000;
  const monthLabel = new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
  const parts: string[] = [];

  let cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
  const endMonth = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));
  while (cursor <= endMonth) {
    const monthStart = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth(), 1));
    const monthEnd = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 0));
    const rangeStart = start > monthStart ? start : monthStart;
    const rangeEnd = end < monthEnd ? end : monthEnd;
    if (rangeStart <= rangeEnd) {
      const days = Math.floor((rangeEnd.getTime() - rangeStart.getTime()) / oneDay) + 1;
      const fullMonth = rangeStart.getUTCDate() === 1 && rangeEnd.getUTCDate() === monthEnd.getUTCDate();
      if (fullMonth) {
        parts.push(`${monthLabel.format(monthStart)} (full month)`);
      } else {
        parts.push(`${monthLabel.format(monthStart)} (${days} days, partial)`);
      }
    }
    cursor = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1));
  }
  return parts.length ? parts.join(" • ") : null;
};

export default function RelativeStrengthClient({ index }: { index: string }) {
  const indexParam = index;
  const indexOptions = INDEX_OPTIONS.map((option) => option.value);
  const indexName = indexOptions.includes(indexParam as (typeof indexOptions)[number]) ? indexParam : "KSE-100";
  const activeIndexOption = INDEX_OPTIONS.find((option) => option.value === indexName) ?? INDEX_OPTIONS[0];

  const [summaryPayload, setSummaryPayload] = useState<RelativeStrengthSummaryPayload | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [sectorPayload, setSectorPayload] = useState<SectorRelativeStrengthPayload | null>(null);
  const [sectorLoading, setSectorLoading] = useState(true);
  const [sectorError, setSectorError] = useState<string | null>(null);
  const [live, setLive] = useState<LivePayload | null>(null);
  const [selectedRsKey, setSelectedRsKey] = useState<RSMetricKey>("RS20D");
  const [stockTab, setStockTab] = useState<StockTab>("leaders");
  const [sectorFilter, setSectorFilter] = useState("All");
  const [positiveAcrossWindows, setPositiveAcrossWindows] = useState(false);

  useEffect(() => {
    const loadLive = async () => {
      try {
        const res = await fetch("/api/outputs/indices_eod.json");
        if (!res.ok) throw new Error(`EOD fetch failed: ${res.status}`);
        const data = normalizeLivePayload(await res.json());
        setLive(data);
      } catch {
        setLive(null);
      }
    };
    loadLive();
  }, []);

  useEffect(() => {
    const loadSummary = async () => {
      try {
        setSummaryLoading(true);
        setSummaryError(null);
        const url = `/api/indices/relative-strength/summary?index=${encodeURIComponent(indexName)}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`RS summary fetch failed: ${res.status}`);
        const data = (await res.json()) as RelativeStrengthSummaryPayload;
        setSummaryPayload(data);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to load relative strength summary";
        setSummaryError(msg);
        setSummaryPayload(null);
      } finally {
        setSummaryLoading(false);
      }
    };
    loadSummary();
  }, [indexName]);

  useEffect(() => {
    const loadSector = async () => {
      try {
        setSectorLoading(true);
        setSectorError(null);
        const url = `/api/indices/relative-strength/sectors?index=${encodeURIComponent(indexName)}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Sector RS fetch failed: ${res.status}`);
        const data = (await res.json()) as SectorRelativeStrengthPayload;
        setSectorPayload(data);
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to load sector relative strength";
        setSectorError(msg);
        setSectorPayload(null);
      } finally {
        setSectorLoading(false);
      }
    };
    loadSector();
  }, [indexName]);

  useEffect(() => {
    setSelectedRsKey(getPrimaryRsKey(summaryPayload?.windowSupport));
    setStockTab("leaders");
    setSectorFilter("All");
    setPositiveAcrossWindows(false);
  }, [indexName, summaryPayload?.windowSupport]);

  const liveIndex = useMemo(() => {
    if (!live?.indices?.length) return null;
    const code = INDEX_CODE_MAP[indexName] || normalizeCode(indexName);
    return (
      live.indices.find(
        (i) =>
          normalizeCode(i.code) === code ||
          normalizeCode(i.index) === code ||
          i.index.toLowerCase() === indexName.toLowerCase()
      ) || null
    );
  }, [live, indexName]);

  const periodCoverageText = useMemo(() => {
    if (!summaryPayload?.period?.from || !summaryPayload?.period?.to) return null;
    return monthCoverageSummary(summaryPayload.period.from, summaryPayload.period.to);
  }, [summaryPayload]);

  const rsCoverageNote = useMemo(() => {
    const support = summaryPayload?.windowSupport;
    if (!support) return null;
    if (support.has60D && support.has90D) return null;
    const missingWindows: string[] = [];
    if (!support.has60D) missingWindows.push(RS_LABELS.RS60D);
    if (!support.has90D) missingWindows.push(RS_LABELS.RS90D);
    if (!missingWindows.length) return null;
    return `${missingWindows.join(" and ")} are unavailable until enough session history is collected.`;
  }, [summaryPayload]);

  const summaryPrimaryKey = useMemo(
    () => getPrimaryRsKey(summaryPayload?.windowSupport),
    [summaryPayload],
  );
  const activeRsKey = isWindowSupported(selectedRsKey, summaryPayload?.windowSupport) ? selectedRsKey : summaryPrimaryKey;
  const activeRsLabel = RS_LABELS[activeRsKey];
  const activeBreadth =
    activeRsKey === "RS20D"
      ? summaryPayload?.rsBreadth.positiveRS20D_pct
      : activeRsKey === "RS60D"
        ? summaryPayload?.rsBreadth.positiveRS60D_pct
        : summaryPayload?.rsBreadth.positiveRS90D_pct;
  const activeAverage =
    activeRsKey === "RS20D"
      ? summaryPayload?.rsAverages.avgRS20D
      : activeRsKey === "RS60D"
        ? summaryPayload?.rsAverages.avgRS60D
        : summaryPayload?.rsAverages.avgRS90D;
  const sectorInterpretation = useMemo(() => {
    if (!sectorPayload?.sectorRS?.length) return null;
    const sorted = sortByRs(sectorPayload.sectorRS, activeRsKey, "desc");
    if (!sorted.length) return null;
    const top = sorted.slice(0, 2).map((row) => row.sector);
    const bottom = sorted.slice(-2).map((row) => row.sector);

    const topText = top.filter(Boolean).join(" and ");
    const bottomText = bottom.filter(Boolean).join(" and ");

    if (!topText || !bottomText) return null;
    return `Sector leadership is concentrated in ${topText}, while weaker participation sits in ${bottomText}.`;
  }, [activeRsKey, sectorPayload]);
  const sectorRankedRows = useMemo(
    () => sortByRs(sectorPayload?.sectorRS || [], activeRsKey, "desc"),
    [sectorPayload, activeRsKey],
  );
  const sectorLaggardRows = useMemo(
    () => sortByRs(sectorPayload?.sectorRS || [], activeRsKey, "asc"),
    [sectorPayload, activeRsKey],
  );
  const sectorLeaderRow = sectorRankedRows[0] || null;
  const sectorLaggardRow = sectorLaggardRows[0] || null;
  const sectorPrimaryValues = useMemo(
    () =>
      sectorPayload?.sectorRS
        ?.map((row) => getRsValue(row, activeRsKey))
        .filter(isFiniteNumber) || [],
    [sectorPayload, activeRsKey],
  );
  const sectorPositivePct = useMemo(() => {
    if (!sectorPrimaryValues.length) return null;
    const positiveCount = sectorPrimaryValues.filter((value) => value > 0).length;
    return (positiveCount / sectorPrimaryValues.length) * 100;
  }, [sectorPrimaryValues]);
  const sectorAverage = useMemo(() => {
    if (!sectorPrimaryValues.length) return null;
    return sectorPrimaryValues.reduce((sum, value) => sum + value, 0) / sectorPrimaryValues.length;
  }, [sectorPrimaryValues]);
  const sectorDispersion = useMemo(() => {
    if (!sectorPrimaryValues.length) return null;
    const mean = sectorPrimaryValues.reduce((sum, value) => sum + value, 0) / sectorPrimaryValues.length;
    const variance =
      sectorPrimaryValues.reduce((sum, value) => sum + (value - mean) ** 2, 0) / sectorPrimaryValues.length;
    return Math.sqrt(variance);
  }, [sectorPrimaryValues]);
  const leaderSpotlight = summaryPayload?.leaders?.[0] || null;
  const laggardSpotlight = summaryPayload?.laggards?.[0] || null;
  const activeLeaderValue = getRsValue(leaderSpotlight, activeRsKey);
  const activeLaggardValue = getRsValue(laggardSpotlight, activeRsKey);
  const leadershipSpread =
    activeLeaderValue != null && activeLaggardValue != null ? activeLeaderValue - activeLaggardValue : null;

  const retailRead = useMemo(() => {
    if (!summaryPayload) return null;

    const sectorText = sectorLeaderRow?.sector
      ? `Best sector: ${sectorLeaderRow.sector}`
      : "Sector leader unavailable";

    if ((activeBreadth ?? 0) >= 60 && (activeAverage ?? 0) > 0) {
      return {
        tone: "positive" as const,
        label: "Leadership is broad",
        action: "Use the leaders tab first. Stocks with positive strength across all windows deserve the cleanest watchlist priority.",
        evidence: [
          `${fmtPctValue(activeBreadth)} beating index`,
          `Average ${fmtPct(activeAverage)}`,
          sectorText,
        ],
      };
    }

    if ((activeBreadth ?? 0) <= 40 || (activeAverage ?? 0) < 0) {
      return {
        tone: "caution" as const,
        label: "Leadership is thin",
        action: "Be selective. Avoid chasing single-name strength unless sector support and liquidity confirm it.",
        evidence: [
          `${fmtPctValue(activeBreadth)} beating index`,
          `Average ${fmtPct(activeAverage)}`,
          sectorText,
        ],
      };
    }

    return {
      tone: "neutral" as const,
      label: "Mixed leadership",
      action: "Focus on stocks holding strength across multiple windows, and avoid names that only lead briefly.",
      evidence: [
        `${fmtPctValue(activeBreadth)} beating index`,
        `Average ${fmtPct(activeAverage)}`,
        sectorText,
      ],
    };
  }, [activeAverage, activeBreadth, sectorLeaderRow, summaryPayload]);

  const sectorOptions = useMemo(() => {
    const sectors = new Set<string>();
    summaryPayload?.leaders?.forEach((row) => {
      if (row.sector) sectors.add(row.sector);
    });
    summaryPayload?.laggards?.forEach((row) => {
      if (row.sector) sectors.add(row.sector);
    });
    return ["All", ...Array.from(sectors).sort((a, b) => a.localeCompare(b))];
  }, [summaryPayload]);

  const stockRows = useMemo(() => {
    const source = stockTab === "leaders" ? summaryPayload?.leaders || [] : summaryPayload?.laggards || [];
    const direction = stockTab === "leaders" ? "desc" : "asc";
    return sortByRs(source, activeRsKey, direction)
      .filter((row) => sectorFilter === "All" || row.sector === sectorFilter)
      .filter((row) => {
        if (!positiveAcrossWindows) return true;
        return (getRsValue(row, "RS20D") ?? -1) > 0 && (getRsValue(row, "RS60D") ?? -1) > 0 && (getRsValue(row, "RS90D") ?? -1) > 0;
      });
  }, [activeRsKey, positiveAcrossWindows, sectorFilter, stockTab, summaryPayload]);

  const rsPulseCards = useMemo(() => {
    if (!summaryPayload) return [];
    const sectorLeaderPrimaryValue = getRsValue(sectorLeaderRow, activeRsKey);

    return [
      {
        label: "Live Level",
        value: liveIndex?.value != null ? fmtMaybe(liveIndex.value, 2) : "—",
        meta:
          liveIndex?.pct_change != null
            ? `${fmtMaybe(liveIndex.change, 2)} / ${fmtMaybe(liveIndex.pct_change, 2)}%`
            : live?.as_of
              ? `As of ${live.as_of}`
              : "Waiting for live snapshot",
      },
      {
        label: `Stocks Beating Index`,
        value: fmtPctValue(activeBreadth),
        meta: `${activeRsLabel} selected window`,
        accent: valueColor(activeBreadth),
      },
      {
        label: "Average Outperformance",
        value: fmtPct(activeAverage),
        meta: `${activeRsLabel} average vs index`,
        accent: rsColor(activeAverage),
      },
      {
        label: "Best Sector",
        value: sectorLeaderRow?.sector || "—",
        meta:
          sectorLeaderRow
            ? `${activeRsLabel} ${fmtPct(sectorLeaderPrimaryValue)}`
            : "Sector leader unavailable",
        accent: rsColor(sectorLeaderPrimaryValue),
      },
      {
        label: "Strongest Stock",
        value: leaderSpotlight?.ticker || "—",
        meta:
          activeLeaderValue != null
            ? `${leaderSpotlight?.sector || "Sector N/A"} · ${activeRsLabel} ${fmtPct(activeLeaderValue)}`
            : "Leader unavailable",
        accent: rsColor(activeLeaderValue),
      },
      {
        label: "Weakest Stock",
        value: laggardSpotlight?.ticker || "—",
        meta:
          activeLaggardValue != null
            ? `${laggardSpotlight?.sector || "Sector N/A"} · ${activeRsLabel} ${fmtPct(activeLaggardValue)}`
            : "Laggard unavailable",
        accent: rsColor(activeLaggardValue),
      },
    ];
  }, [
    summaryPayload,
    liveIndex,
    live,
    activeAverage,
    activeBreadth,
    activeLeaderValue,
    activeLaggardValue,
    activeRsKey,
    activeRsLabel,
    sectorLeaderRow,
    leaderSpotlight,
    laggardSpotlight,
  ]);

  const sectorSpotlight = useMemo(() => {
    return {
      leaders: sectorRankedRows.slice(0, 3),
      laggards: sectorLaggardRows.slice(0, 3),
    };
  }, [sectorRankedRows, sectorLaggardRows]);

  return (
    <>
      <section className="page-header page-header--accent">
        <div className="page-header-container">
          <div className="rs-hero-row">
            <div>
              <h1 className="page-title">{indexName} • Relative Strength</h1>
              <p className="page-subtitle">Stocks and sectors beating this index across 15, 30, and 45 sessions</p>
            </div>
            <div>
              <Link href={`/indices/${encodeURIComponent(indexName)}`} className="ui-button ui-button-ghost">
                Back to Index Dashboard
              </Link>
            </div>
          </div>
          {liveIndex && (
            <div style={{ marginTop: "0.75rem", color: "var(--color-text-muted)", fontSize: "0.9rem" }}>
              Live {indexName}: {fmtMaybe(liveIndex.value, 2)}{" "}
              ({fmtMaybe(liveIndex.change, 2)} / {fmtMaybe(liveIndex.pct_change, 2)}%)
              {live?.as_of ? ` · As of ${live.as_of}` : ""}
            </div>
          )}
          <nav className="index-switcher rs-index-switcher" aria-label="Switch relative strength index">
            {INDEX_OPTIONS.map((option) => (
              <Link
                key={option.value}
                href={`/indices/${encodeURIComponent(option.value)}/relative-strength`}
                className={`index-switcher-link${option.value === indexName ? " index-switcher-link--active" : ""}`}
                aria-current={option.value === indexName ? "page" : undefined}
              >
                <span>{option.label}</span>
                <em>{option.description}</em>
              </Link>
            ))}
          </nav>
        </div>
      </section>

      <section className="strategy-detail-section">
        <div className="strategy-detail-container">
          <div className="rs-control-bar">
            <div>
              <div className="rs-control-title">Active Window</div>
              <div className="rs-control-sub">Rank stocks and sectors by one session window at a time.</div>
            </div>
            <div className="rs-window-tabs" role="tablist" aria-label="Relative strength window">
              {RS_WINDOW_OPTIONS.map((option) => {
                const supported = isWindowSupported(option.key, summaryPayload?.windowSupport);
                return (
                  <button
                    key={option.key}
                    type="button"
                    className={`rs-window-tab${activeRsKey === option.key ? " rs-window-tab--active" : ""}`}
                    onClick={() => setSelectedRsKey(option.key)}
                    disabled={!supported}
                  >
                    <span>{option.label}</span>
                    {!supported && <em>Waiting for data</em>}
                  </button>
                );
              })}
            </div>
          </div>

          {retailRead && (
            <div className={`rs-retail-read rs-retail-read--${retailRead.tone}`}>
              <div>
                <div className="rs-retail-kicker">Retail Read</div>
                <div className="rs-retail-title">{retailRead.label}</div>
                <p>{retailRead.action}</p>
              </div>
              <div className="rs-retail-evidence">
                {retailRead.evidence.map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
            </div>
          )}

          {!summaryLoading && !summaryError && summaryPayload && (
            <div style={{ marginTop: "0.6rem", marginBottom: "1.4rem" }}>
              <div style={{ fontWeight: 700, fontSize: "1.05rem", marginBottom: "0.6rem" }}>Leadership Pulse</div>
              <div style={{ display: "grid", gap: "1rem", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))" }}>
                {rsPulseCards.map((card) => (
                  <div
                    key={card.label}
                    className="rs-card"
                  >
                    <div className="rs-card-label">{card.label}</div>
                    <div className="rs-card-value" style={{ color: card.accent || "inherit" }}>
                      {card.value}
                    </div>
                    <div className="rs-card-sub">{card.meta}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginTop: "0.6rem" }}>
            <div style={{ fontWeight: 700, fontSize: "1.05rem", marginBottom: "0.6rem" }}>
              Selected Window Details
            </div>
            {summaryLoading && (
              <div style={{ color: "var(--color-text-muted)" }}>Loading strong-stock summary...</div>
            )}
            {summaryError && (
              <div style={{ color: "var(--color-danger)" }}>{summaryError}</div>
            )}
            {!summaryLoading && !summaryError && summaryPayload && (
              <>
                {periodCoverageText && (
                  <div style={{ marginBottom: "0.75rem", color: "var(--color-text-muted)", fontSize: "0.9rem" }}>
                    Coverage: {periodCoverageText}
                  </div>
                )}
                {rsCoverageNote && (
                  <div style={{ marginBottom: "0.75rem", color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
                    {rsCoverageNote}
                  </div>
                )}
                <div style={{ display: "grid", gap: "1.2rem", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
                  <div
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "16px",
                      padding: "1rem",
                      background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>Stocks Beating Index</div>
                    <div className="rs-card-value" style={{ color: valueColor(activeBreadth) }}>
                      {fmtPctValue(activeBreadth)}
                    </div>
                    <div className="rs-card-sub">More stocks beating the index means leadership is healthier.</div>
                  </div>

                  <div
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "16px",
                      padding: "1rem",
                      background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>Average Outperformance</div>
                    <div className="rs-card-value" style={{ color: rsColor(activeAverage) }}>
                      {fmtPct(activeAverage)}
                    </div>
                    <div className="rs-card-sub">Average stock performance versus {activeIndexOption.label} over {activeRsLabel}.</div>
                  </div>

                  <div
                    style={{
                      border: "1px solid var(--color-border)",
                      borderRadius: "16px",
                      padding: "1rem",
                      background: "linear-gradient(140deg, rgba(20,32,45,0.9), rgba(12,20,30,0.95))",
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>Sector Leadership ({activeRsLabel})</div>
                    <div style={{ display: "grid", gap: "0.5rem", marginTop: "0.6rem" }}>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ color: "var(--color-text-muted)" }}>Best</span>
                        <span style={{ fontWeight: 700 }}>{sectorLeaderRow?.sector || "—"}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ color: "var(--color-text-muted)" }}>{activeRsLabel}</span>
                        <span style={{ fontWeight: 700, color: rsColor(getRsValue(sectorLeaderRow, activeRsKey)) }}>
                          {fmtPct(getRsValue(sectorLeaderRow, activeRsKey))}
                        </span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "0.4rem" }}>
                        <span style={{ color: "var(--color-text-muted)" }}>Worst</span>
                        <span style={{ fontWeight: 700 }}>{sectorLaggardRow?.sector || "—"}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ color: "var(--color-text-muted)" }}>{activeRsLabel}</span>
                        <span style={{ fontWeight: 700, color: rsColor(getRsValue(sectorLaggardRow, activeRsKey)) }}>
                          {fmtPct(getRsValue(sectorLaggardRow, activeRsKey))}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ display: "grid", gap: "1rem", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", marginTop: "1.2rem" }}>
                  <div className="rs-card">
                    <div className="rs-card-label">Leadership Gap</div>
                    <div
                      className="rs-card-value"
                      style={{ color: rsColor(leadershipSpread) }}
                    >
                      {fmtPct(leadershipSpread)}
                    </div>
                    <div className="rs-card-sub">Gap between strongest and weakest stock on {activeRsLabel}</div>
                  </div>

                  <div className="rs-card">
                    <div className="rs-card-label">Best Sector Weight</div>
                    <div className="rs-card-value">
                      {sectorSpotlight.leaders[0]?.weightPct != null ? fmtPctValue(sectorSpotlight.leaders[0].weightPct, 2) : "—"}
                    </div>
                    <div className="rs-card-sub">{sectorSpotlight.leaders[0]?.sector || "Sector leader unavailable"}</div>
                  </div>

                  <div className="rs-card">
                    <div className="rs-card-label">Worst Sector</div>
                    <div className="rs-card-value">{sectorLaggardRow?.sector || "—"}</div>
                    <div className="rs-card-sub" style={{ color: rsColor(getRsValue(sectorLaggardRow, activeRsKey)) }}>
                      {sectorLaggardRow
                        ? `${activeRsLabel} ${fmtPct(getRsValue(sectorLaggardRow, activeRsKey))}`
                        : "Sector laggard unavailable"}
                    </div>
                  </div>
                </div>

                <div className="rs-card rs-stock-panel">
                  <div className="rs-stock-panel-head">
                    <div>
                      <div style={{ fontWeight: 700 }}>Stocks to Inspect Next</div>
                      <div className="rs-section-subtitle">
                        Sorts by {activeRsLabel}. Use leaders for watchlists and laggards for avoid/reversal checks.
                      </div>
                    </div>
                    <div className="rs-stock-tabs" role="tablist" aria-label="Stock relative strength view">
                      <button
                        type="button"
                        className={`rs-stock-tab${stockTab === "leaders" ? " rs-stock-tab--active" : ""}`}
                        onClick={() => setStockTab("leaders")}
                      >
                        Leaders
                      </button>
                      <button
                        type="button"
                        className={`rs-stock-tab${stockTab === "laggards" ? " rs-stock-tab--active" : ""}`}
                        onClick={() => setStockTab("laggards")}
                      >
                        Laggards
                      </button>
                    </div>
                  </div>
                  <div className="rs-stock-filters">
                    <label>
                      <span>Sector</span>
                      <select value={sectorFilter} onChange={(event) => setSectorFilter(event.target.value)}>
                        {sectorOptions.map((sector) => (
                          <option key={sector} value={sector}>
                            {sector}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="rs-stock-check">
                      <input
                        type="checkbox"
                        checked={positiveAcrossWindows}
                        onChange={(event) => setPositiveAcrossWindows(event.target.checked)}
                      />
                      <span>Only positive across all windows</span>
                    </label>
                  </div>
                  {stockRows.length === 0 ? (
                    <div style={{ color: "var(--color-text-muted)" }}>
                      No stocks match the selected filters.
                    </div>
                  ) : (
                    <div className="rs-table-scroll">
                      <table className="rs-stock-table">
                        <thead>
                          <tr>
                            <th>Ticker</th>
                            <th>Sector</th>
                            <th>{activeRsLabel}</th>
                            <th>Other Windows</th>
                            <th>Next</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stockRows.map((row) => (
                            <tr key={row.ticker}>
                              <td>
                                <Link href={`/stock/${encodeURIComponent(row.ticker)}`}>
                                  {row.ticker}
                                </Link>
                              </td>
                              <td>{row.sector}</td>
                              <td style={{ color: rsColor(getRsValue(row, activeRsKey)), fontWeight: 800 }}>
                                {fmtPct(getRsValue(row, activeRsKey))}
                              </td>
                              <td>
                                <span>{RS_WINDOW_OPTIONS.filter((option) => option.key !== activeRsKey).map((option) => `${option.shortLabel} ${fmtPct(getRsValue(row, option.key), 1)}`).join(" · ")}</span>
                              </td>
                              <td>
                                <Link href={`/stock/${encodeURIComponent(row.ticker)}`} className="rs-next-link">
                                  Open Stock
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <div style={{ marginTop: "1.8rem" }}>
            <div className="rs-section-header">
              <div>
                <div className="rs-section-title">
                  Strong Sectors
                  <span
                    className="rs-tooltip"
                    title="This compares sector returns versus the index across 15, 30, and 45-session windows. Ranking uses the best available window."
                  >
                    i
                  </span>
                </div>
                <div className="rs-section-subtitle">
                  Sectors beating or lagging {indexName} across 15, 30, and 45 sessions
                </div>
              </div>
            </div>

            {sectorLoading && (
              <div style={{ color: "var(--color-text-muted)" }}>Loading strong-sector data...</div>
            )}
            {sectorError && (
              <div style={{ color: "var(--color-danger)" }}>{sectorError}</div>
            )}
            {!sectorLoading && !sectorError && !sectorPayload && (
              <div style={{ color: "var(--color-text-muted)" }}>
                Strong-sector data is unavailable for this index session.
              </div>
            )}
            {!sectorLoading && !sectorError && sectorPayload && (
              <>
                <div className="rs-summary-row">
                  <div className="rs-card">
                    <div className="rs-card-label">Sectors Beating Index</div>
                    <div className="rs-card-value" style={{ color: valueColor(sectorPositivePct) }}>
                      {fmtPctValue(sectorPositivePct, 1)}
                    </div>
                    <div className="rs-card-sub">More positive sectors means leadership is less narrow.</div>
                  </div>

                  <div className="rs-card">
                    <div className="rs-card-label">Best Sector</div>
                    <div className="rs-card-value">{sectorLeaderRow?.sector || "—"}</div>
                    <div className="rs-card-sub" style={{ color: rsColor(getRsValue(sectorLeaderRow, activeRsKey)) }}>
                      {activeRsLabel} {fmtPct(getRsValue(sectorLeaderRow, activeRsKey))}
                    </div>
                  </div>

                  <div className="rs-card">
                    <div className="rs-card-label">Worst Sector</div>
                    <div className="rs-card-value">{sectorLaggardRow?.sector || "—"}</div>
                    <div className="rs-card-sub" style={{ color: rsColor(getRsValue(sectorLaggardRow, activeRsKey)) }}>
                      {activeRsLabel} {fmtPct(getRsValue(sectorLaggardRow, activeRsKey))}
                    </div>
                  </div>

                  <div className="rs-card">
                    <div className="rs-card-label">Average Sector Strength</div>
                    <div className="rs-card-value" style={{ color: rsColor(sectorAverage) }}>
                      {fmtPct(sectorAverage)}
                    </div>
                  </div>

                  <div className="rs-card">
                    <div className="rs-card-label">Leadership Gap</div>
                    <div className="rs-card-value" style={{ color: rsColor(sectorDispersion) }}>
                      {fmtPct(sectorDispersion)}
                    </div>
                  </div>
                </div>
                {sectorInterpretation && (
                  <div style={{ marginTop: "0.65rem", color: "var(--color-text-muted)", fontSize: "0.9rem" }}>
                    {sectorInterpretation}
                  </div>
                )}

                {!!sectorSpotlight.leaders.length && (
                  <div style={{ display: "grid", gap: "1rem", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", marginTop: "1rem" }}>
                    <div className="rs-card">
                      <div style={{ fontWeight: 700, marginBottom: "0.6rem" }}>Top Sectors</div>
                      <div style={{ display: "grid", gap: "0.55rem" }}>
                        {sectorSpotlight.leaders.map((row) => (
                          <div
                            key={row.sector}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "0.5rem 0.6rem",
                              borderRadius: "10px",
                              background: "rgba(15, 23, 42, 0.5)",
                              border: "1px solid rgba(148, 163, 184, 0.2)",
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: 600 }}>{row.sector}</div>
                              <div style={{ color: "var(--color-text-muted)", fontSize: "0.8rem" }}>
                                Weight {fmtPctValue(row.weightPct, 2)} · {activeRsLabel} {fmtPct(getRsValue(row, activeRsKey))}
                              </div>
                            </div>
                            <div style={{ fontWeight: 700, color: rsColor(getRsValue(row, activeRsKey)) }}>
                              {fmtPct(getRsValue(row, activeRsKey))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rs-card">
                      <div style={{ fontWeight: 700, marginBottom: "0.6rem" }}>Weakest Sectors</div>
                      <div style={{ display: "grid", gap: "0.55rem" }}>
                        {sectorSpotlight.laggards.map((row) => (
                          <div
                            key={row.sector}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "0.5rem 0.6rem",
                              borderRadius: "10px",
                              background: "rgba(15, 23, 42, 0.5)",
                              border: "1px solid rgba(148, 163, 184, 0.2)",
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: 600 }}>{row.sector}</div>
                              <div style={{ color: "var(--color-text-muted)", fontSize: "0.8rem" }}>
                                Weight {fmtPctValue(row.weightPct, 2)} · {activeRsLabel} {fmtPct(getRsValue(row, activeRsKey))}
                              </div>
                            </div>
                            <div style={{ fontWeight: 700, color: rsColor(getRsValue(row, activeRsKey)) }}>
                              {fmtPct(getRsValue(row, activeRsKey))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {sectorPayload.sectorRS.length === 0 ? (
                  <div style={{ marginTop: "1rem", color: "var(--color-text-muted)" }}>
                    Sector strength data is not sufficient for visualization.
                  </div>
                ) : (
                  <div className="rs-card" style={{ marginTop: "1.2rem" }}>
                    <div style={{ fontWeight: 700, marginBottom: "0.6rem" }}>Sector Strength by {activeRsLabel}</div>
                    <div className="rs-chart-scale">
                      <span>Lagging index</span>
                      <span>Beating index</span>
                    </div>
                    <div className="rs-bar-scroll">
                      <div className="rs-bar-scroll-inner">
                        <div className="rs-bar-list">
                          {sectorRankedRows.map((row) => {
                            const primaryValue = getRsValue(row, activeRsKey) ?? 0;
                            const magnitude = Math.min(Math.abs(primaryValue) * 50, 50);
                            const barWidth = `${magnitude}%`;
                            const barLeft = primaryValue >= 0 ? "50%" : `calc(50% - ${magnitude}%)`;
                            return (
                              <div key={row.sector} className="rs-bar-row">
                                <div className="rs-bar-label">
                                  <span>{row.sector}</span>
                                  {typeof row.weightPct === "number" && (
                                    <span className="rs-bar-weight">{fmt(row.weightPct, 1)}%</span>
                                  )}
                                </div>
                                <div className="rs-bar-track">
                                  <div
                                    className="rs-bar-fill"
                                    style={{
                                      width: barWidth,
                                      left: barLeft,
                                      background: primaryValue >= 0 ? "var(--color-success)" : "var(--color-danger)",
                                    }}
                                  />
                                </div>
                                <div className="rs-bar-value" style={{ color: rsColor(primaryValue) }}>
                                  {fmtPct(primaryValue)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      <style jsx>{`
        .rs-hero-row {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          flex-wrap: wrap;
        }
        .rs-index-switcher {
          margin-top: 1.2rem;
        }
        .rs-control-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          margin-bottom: 1rem;
          padding: 0.75rem;
          border: 1px solid rgba(148, 163, 184, 0.18);
          border-radius: 12px;
          background: rgba(8, 14, 22, 0.72);
        }
        .rs-control-title {
          font-weight: 800;
        }
        .rs-control-sub {
          margin-top: 0.2rem;
          color: var(--color-text-muted);
          font-size: 0.84rem;
        }
        .rs-window-tabs,
        .rs-stock-tabs {
          display: inline-flex;
          gap: 0.35rem;
          padding: 0.3rem;
          border: 1px solid rgba(148, 163, 184, 0.2);
          border-radius: 10px;
          background: rgba(15, 23, 42, 0.52);
        }
        .rs-window-tab,
        .rs-stock-tab {
          min-height: 34px;
          border: 0;
          border-radius: 8px;
          padding: 0.35rem 0.65rem;
          background: transparent;
          color: rgba(226, 232, 240, 0.74);
          font: inherit;
          font-size: 0.82rem;
          font-weight: 800;
          cursor: pointer;
        }
        .rs-window-tab {
          display: grid;
          gap: 0.1rem;
          text-align: left;
        }
        .rs-window-tab em {
          color: var(--color-text-muted);
          font-size: 0.68rem;
          font-style: normal;
          font-weight: 650;
        }
        .rs-window-tab--active,
        .rs-stock-tab--active {
          background: rgba(138, 180, 248, 0.16);
          color: rgba(248, 250, 252, 0.98);
          box-shadow: inset 0 0 0 1px rgba(138, 180, 248, 0.28);
        }
        .rs-window-tab:disabled {
          cursor: not-allowed;
          opacity: 0.48;
        }
        .rs-retail-read {
          display: grid;
          grid-template-columns: minmax(0, 1.35fr) minmax(220px, 0.75fr);
          gap: 1rem;
          align-items: center;
          margin-bottom: 1.2rem;
          border: 1px solid rgba(148, 163, 184, 0.2);
          border-radius: 12px;
          padding: 1.1rem;
          background: linear-gradient(140deg, rgba(20, 32, 45, 0.96), rgba(12, 20, 30, 0.98));
        }
        .rs-retail-read--positive {
          border-color: rgba(34, 197, 94, 0.35);
        }
        .rs-retail-read--caution {
          border-color: rgba(248, 113, 113, 0.38);
        }
        .rs-retail-read--neutral {
          border-color: rgba(138, 180, 248, 0.32);
        }
        .rs-retail-kicker {
          color: rgba(191, 219, 254, 0.86);
          font-size: 0.74rem;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }
        .rs-retail-title {
          margin-top: 0.35rem;
          color: rgba(248, 250, 252, 0.98);
          font-size: 1.3rem;
          font-weight: 850;
        }
        .rs-retail-read p {
          margin: 0.35rem 0 0;
          color: rgba(226, 232, 240, 0.74);
          line-height: 1.55;
        }
        .rs-retail-evidence {
          display: grid;
          gap: 0.45rem;
        }
        .rs-retail-evidence span {
          min-height: 34px;
          display: flex;
          align-items: center;
          padding: 0.4rem 0.6rem;
          border: 1px solid rgba(148, 163, 184, 0.16);
          border-radius: 8px;
          background: rgba(15, 23, 42, 0.56);
          color: rgba(226, 232, 240, 0.78);
          font-size: 0.82rem;
          font-weight: 700;
        }
        .rs-section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.8rem;
        }
        .rs-section-title {
          font-weight: 700;
          font-size: 1.05rem;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }
        .rs-section-subtitle {
          color: var(--color-text-muted);
          font-size: 0.85rem;
          margin-top: 0.25rem;
        }
        .rs-tooltip {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 18px;
          height: 18px;
          border-radius: 999px;
          border: 1px solid var(--color-border);
          color: var(--color-text-muted);
          font-size: 0.7rem;
          cursor: help;
          background: rgba(15, 23, 42, 0.6);
        }
        .rs-summary-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-top: 1rem;
        }
        .rs-card {
          border: 1px solid var(--color-border);
          border-radius: 16px;
          padding: 1rem;
          background: linear-gradient(140deg, rgba(20, 32, 45, 0.9), rgba(12, 20, 30, 0.95));
        }
        .rs-card-label {
          color: var(--color-text-muted);
          font-size: 0.8rem;
        }
        .rs-card-value {
          font-weight: 700;
          font-size: 1.1rem;
          margin-top: 0.4rem;
        }
        .rs-card-sub {
          color: var(--color-text-muted);
          font-size: 0.85rem;
          margin-top: 0.3rem;
          font-variant-numeric: tabular-nums;
        }
        .rs-stock-panel {
          margin-top: 1.2rem;
        }
        .rs-stock-panel-head {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 1rem;
          margin-bottom: 0.9rem;
        }
        .rs-stock-filters {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          align-items: center;
          margin-bottom: 0.8rem;
        }
        .rs-stock-filters label {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          color: var(--color-text-muted);
          font-size: 0.82rem;
          font-weight: 750;
        }
        .rs-stock-filters select {
          min-height: 34px;
          border: 1px solid rgba(148, 163, 184, 0.24);
          border-radius: 8px;
          background: rgba(8, 14, 22, 0.9);
          color: var(--color-text);
          padding: 0.25rem 0.55rem;
        }
        .rs-stock-check input {
          width: 16px;
          height: 16px;
        }
        .rs-table-scroll {
          overflow-x: auto;
        }
        .rs-stock-table {
          width: 100%;
          min-width: 720px;
          border-collapse: collapse;
          font-size: 0.9rem;
        }
        .rs-stock-table th {
          padding: 0 0.6rem 0.45rem 0;
          color: var(--color-text-muted);
          text-align: left;
          font-weight: 750;
        }
        .rs-stock-table td {
          padding: 0.55rem 0.6rem 0.55rem 0;
          border-top: 1px solid var(--color-border);
          color: rgba(226, 232, 240, 0.78);
          font-variant-numeric: tabular-nums;
        }
        .rs-stock-table a {
          color: var(--blue-text);
          font-weight: 800;
          text-decoration: none;
        }
        .rs-next-link {
          display: inline-flex;
          min-height: 30px;
          align-items: center;
          padding: 0.25rem 0.55rem;
          border: 1px solid rgba(138, 180, 248, 0.34);
          border-radius: 8px;
          background: rgba(138, 180, 248, 0.1);
        }
        .rs-chart-scale {
          display: flex;
          justify-content: space-between;
          margin: -0.2rem 0 0.7rem;
          color: var(--color-text-muted);
          font-size: 0.76rem;
          font-weight: 700;
        }
        .rs-bar-scroll {
          overflow-x: auto;
        }
        .rs-bar-scroll-inner {
          min-width: 520px;
        }
        .rs-bar-list {
          display: grid;
          gap: 0.6rem;
        }
        .rs-bar-row {
          display: grid;
          grid-template-columns: 150px 1fr 90px;
          gap: 0.75rem;
          align-items: center;
          font-size: 0.85rem;
        }
        .rs-bar-label {
          color: var(--color-text);
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.5rem;
        }
        .rs-bar-weight {
          color: var(--color-text-muted);
          font-size: 0.75rem;
          font-weight: 500;
          white-space: nowrap;
        }
        .rs-bar-track {
          background: rgba(15, 23, 42, 0.8);
          border: 1px solid var(--color-border);
          border-radius: 999px;
          height: 12px;
          overflow: hidden;
          position: relative;
        }
        .rs-bar-track::before {
          content: "";
          position: absolute;
          left: 50%;
          top: -2px;
          bottom: -2px;
          width: 1px;
          background: rgba(148, 163, 184, 0.5);
        }
        .rs-bar-fill {
          height: 100%;
          border-radius: 999px;
          transition: width 0.4s ease, left 0.4s ease;
          position: absolute;
          top: 0;
          bottom: 0;
        }
        .rs-bar-value {
          text-align: right;
          font-variant-numeric: tabular-nums;
          color: var(--color-text-muted);
        }
        @media (max-width: 900px) {
          .rs-control-bar,
          .rs-retail-read,
          .rs-stock-panel-head {
            display: grid;
          }
          .rs-window-tabs,
          .rs-stock-tabs {
            width: 100%;
            overflow-x: auto;
          }
          .rs-summary-row {
            display: flex;
            overflow-x: auto;
            scroll-snap-type: x mandatory;
            padding-bottom: 0.5rem;
          }
          .rs-card {
            min-width: 220px;
            scroll-snap-align: start;
          }
        }
      `}</style>
    </>
  );
}
